import React, { useEffect } from 'react';
import { useSecurityMode } from './SecurityModeContext';
import { allSatisfied, normalize, registeredAreaOf, securedAreaId } from './securedRules';
import type { SecuredConfig } from './securedRules';

// The rules themselves live in `securedRules.ts` (no React, so they stay unit-testable); they are
// re-exported here because this module is where the rest of the app already imports them from.
export type { RuleItem, RuleInput, SecuredConfig } from './securedRules';
export { isSecuredVisible } from './securedRules';

export interface SecuredDecoration {
  /** security mode is on */
  enabled: boolean;
  /** all show-rules satisfied (true when no show rules supplied) */
  visible: boolean;
  /** null when no editable rules supplied, else whether all are satisfied */
  editable: boolean | null;
  /** wraps children with the outline/chip when enabled; pass-through otherwise */
  decorate: (children: React.ReactNode) => React.ReactNode;
}

export function useSecuredDecoration(config?: SecuredConfig): SecuredDecoration {
  const { registry, enabled } = useSecurityMode();
  const visibleItems = normalize(config?.visible);
  const editItems = normalize(config?.editable);
  const visible = visibleItems.length === 0 || allSatisfied(visibleItems);
  const editable = editItems.length === 0 ? null : allSatisfied(editItems);
  // Registered and rendered under the full id (with the instance): see registeredAreaOf.
  const id = config?.id ? securedAreaId(config) : undefined;

  useEffect(() => {
    if (!registry || !config?.id) return;
    const area = registeredAreaOf(config);
    registry.registerArea(area);
    return () => registry.unregisterArea(area.id);
  });

  const decorate = (children: React.ReactNode): React.ReactNode => {
    if (!enabled || !config) {
      return visible ? children : null;
    }
    return (
      <SecuredDecorationBox id={id} label={config.label} visible={visible} editable={editable} note={config.note}>
        {visible ? children : null}
      </SecuredDecorationBox>
    );
  };

  return { enabled, visible, editable, decorate };
}

const borderHex = (visible: boolean, editable: boolean | null): string => {
  if (!visible) return '#ef4444';
  if (editable === null) return '#9ca3af';
  if (editable) return '#22c55e';
  return '#eab308';
};

const borderClass = (visible: boolean, editable: boolean | null): string => {
  if (!visible) return 'border-red-500';
  if (editable === null) return 'border-gray-400';
  if (editable) return 'border-green-500';
  return 'border-yellow-500';
};

interface BoxProps {
  id: string;
  label?: string;
  visible: boolean;
  editable: boolean | null;
  note?: string;
  className?: string;
  children: React.ReactNode;
}

export const SecuredDecorationBox: React.FC<BoxProps> = ({ id, label, visible, editable, note, className, children }) => {
  const { selectAreaId, selectedAreaId } = useSecurityMode();
  const isSelected = selectedAreaId === id;
  const border = borderClass(visible, editable);
  const selectedStyle = isSelected ? { boxShadow: `0 0 0 3px ${borderHex(visible, editable)}, 0 0 0 5px white` } : undefined;

  return (
    <div data-security-area={id} className={`relative my-1 border-2 border-dashed ${border} rounded p-2 ${className ?? ''}`} style={selectedStyle}>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          selectAreaId(isSelected ? null : id);
        }}
        className={`absolute -top-3 left-2 px-1.5 py-0.5 text-[10px] font-mono bg-white border ${border} rounded cursor-pointer leading-tight`}
      >
        {id}
        {visible ? (editable === null ? ' · view' : editable ? ' · RW' : ' · RO') : ' · hidden'}
      </button>
      {visible ? children : <div className="text-xs text-gray-500 italic py-2 text-center">{label ? `(hidden) ${label}` : '(hidden)'}</div>}
      {note && <div className="mt-1 text-[10px] font-mono text-gray-500 leading-tight">{note}</div>}
    </div>
  );
};
